'use client'

import { useFavorite } from 'favorite/hooks/useFavorite'
import { useFavoritesButtonNotifications } from 'favorite/hooks/useFavoritesButtonNotifications'
import type { FavoriteProduct, FavoriteProps } from 'favorite/types/types'
import { Button } from 'fukku/Button'
import { useLabels } from 'labels/hooks/useLabels/useLabels'

type FavoriteIconType =
	| 'favoff-l'
	| 'favoff-s'
	| 'favoff-l-small'
	| 'favoff-s-small'

export const FavoriteButton = ({
	productId,
	className = '',
	onClick = () => {
		/* do nothing */
	},
	dataTestIds,
	secondary = false,
	border = false,
	notification = false,
	haveToShowLabel = false,
	selectedColorId,
	iconSize,
	location,
}: FavoriteProps) => {
	const { t } = useLabels()

	const { get, add, remove } = useFavorite({ location })

	const favoriteProduct: FavoriteProduct = {
		ref: productId,
		color: selectedColorId,
	}

	const isFavorite = get(favoriteProduct)

	const { hasReachedLimit, notifyLimit, notifyRemovedFavorite } =
		useFavoritesButtonNotifications(notification, isFavorite)

	const buttonLabel = t(
		`accessibility.wishlist.${isFavorite ? 'remove' : 'add'}.button`
	)

	const iconVariants: { [key: string]: { [key: string]: FavoriteIconType } } = {
		true: {
			small: 'favoff-s-small',
			default: 'favoff-s',
		},
		false: {
			small: 'favoff-l-small',
			default: 'favoff-l',
		},
	}

	const favoriteIconState = String(!!isFavorite)
	const favoriteIconSize = iconSize || 'default'
	const favoriteIcon = iconVariants[favoriteIconState][favoriteIconSize]

	const handleClick = async () => {
		try {
			if (await hasReachedLimit()) {
				notifyLimit()
				return
			}

			isFavorite ? await remove(favoriteProduct) : await add(favoriteProduct)
			notifyRemovedFavorite(favoriteProduct)
			onClick()
		} catch (e) {
			// eslint-disable-next-line no-console
			console.error(`Failed to ${isFavorite ? 'remove' : 'add'} favorite`, e)
		}
	}

	return (
		<Button
			label={buttonLabel}
			icon={favoriteIcon}
			className={className}
			secondary={secondary}
			border={border}
			onClick={handleClick}
			data-testid={isFavorite ? dataTestIds?.active : dataTestIds?.inactive}
		>
			{haveToShowLabel ? buttonLabel : ''}
		</Button>
	)
}
